import { z } from 'zod';
import { apiDelete, apiGet, apiPost, demoMode } from './api';
import { defaultSalonSettings } from './salonSettings';

export const DEMO_SCHEDULE_BLOCKS_KEY = 'mnc-demo-schedule-blocks';

export const ScheduleBlockSchema = z.object({
  id: z.string(),
  staff_id: z.string().nullable(),
  staff_name: z.string().nullable().optional(),
  starts_at: z.string(),
  ends_at: z.string(),
  reason: z.string()
});
export type ScheduleBlock = z.infer<typeof ScheduleBlockSchema>;
export type ScheduleBlockStaff = { id: string; name: string };
export type ScheduleBlocksData = { blocks: ScheduleBlock[]; staff: ScheduleBlockStaff[] };

export function readDemoScheduleBlocks(): ScheduleBlock[] {
  try {
    const value = JSON.parse(localStorage.getItem(DEMO_SCHEDULE_BLOCKS_KEY) ?? '[]');
    return z.array(ScheduleBlockSchema).parse(value);
  } catch { return []; }
}

function writeDemo(rows: ScheduleBlock[]): void {
  localStorage.setItem(DEMO_SCHEDULE_BLOCKS_KEY, JSON.stringify(rows));
}

export async function fetchScheduleBlocks(): Promise<ScheduleBlocksData> {
  if (demoMode) {
    return {
      blocks: readDemoScheduleBlocks().filter((row) => Date.parse(row.ends_at) > Date.now()),
      staff: defaultSalonSettings().staff.map(({ id, name }) => ({ id, name }))
    };
  }
  const data = await apiGet<{ blocks: unknown; staff: ScheduleBlockStaff[] }>('/owner/schedule-blocks');
  return { blocks: z.array(ScheduleBlockSchema).parse(data.blocks), staff: data.staff ?? [] };
}

export async function createScheduleBlock(input: {
  staffId: string | null;
  startsAt: string;
  endsAt: string;
  reason: string;
}): Promise<void> {
  if (demoMode) {
    writeDemo([...readDemoScheduleBlocks(), {
      id: crypto.randomUUID(),
      staff_id: input.staffId,
      staff_name: null,
      starts_at: input.startsAt,
      ends_at: input.endsAt,
      reason: input.reason
    }]);
    return;
  }
  await apiPost('/owner/schedule-blocks', input);
}

export async function deleteScheduleBlock(id: string): Promise<void> {
  if (demoMode) {
    writeDemo(readDemoScheduleBlocks().filter((row) => row.id !== id));
    return;
  }
  await apiDelete(`/owner/schedule-blocks?id=${encodeURIComponent(id)}`);
}
